import { differenceInDays } from 'date-fns';
import type { PackageSearchFilters } from '../types/marketplaceSearch';
import { parseFlexibleDate } from './dateParse';

type PackageLike = {
  title: string;
  description: string;
  category: string;
  type?: 'B2B' | 'B2C';
  departure: string;
  cities?: string[] | null;
  countries?: string[] | null;
  departures?: Array<{ departureDate: string; returnDate: string }> | null;
  airline?: { id: number } | null;
};

function tripDays(dep: { departureDate: string; returnDate: string }): number {
  const a = parseFlexibleDate(dep.departureDate);
  const b = parseFlexibleDate(dep.returnDate);
  if (isNaN(a.getTime()) || isNaN(b.getTime())) return -1;
  return Math.max(0, differenceInDays(b, a));
}

function matchesDuration(days: number, duration: string): boolean {
  switch (duration) {
    case '1-3':
      return days >= 0 && days <= 3;
    case '4-7':
      return days >= 4 && days <= 7;
    case '8-14':
      return days >= 8 && days <= 14;
    case '15+':
      return days >= 15;
    default:
      return true;
  }
}

export function filterPackagesBySearch<T extends PackageLike>(
  packages: T[],
  filters: PackageSearchFilters
): T[] {
  const dest = filters.destination.trim().toLowerCase();
  const monthNum = parseInt(filters.month, 10);
  const hasMonth = !Number.isNaN(monthNum) && monthNum >= 1 && monthNum <= 12;
  const hasDuration = Boolean(filters.duration);
  const hasCategory = Boolean(filters.category);
  const hasPackageType = Boolean(filters.packageType);
  const airlineIdNum = parseInt(filters.airlineId, 10);
  const hasAirline = !Number.isNaN(airlineIdNum);

  if (
    !dest &&
    !hasMonth &&
    !hasDuration &&
    !hasCategory &&
    !hasPackageType &&
    !hasAirline
  ) {
    return packages;
  }

  return packages.filter((p) => {
    if (hasPackageType && p.type && p.type !== filters.packageType) {
      return false;
    }

    if (hasAirline) {
      if (!p.airline || p.airline.id !== airlineIdNum) return false;
    }

    if (hasCategory && p.category !== filters.category) {
      return false;
    }

    if (dest) {
      const extra = [
        ...(Array.isArray(p.cities) ? p.cities : []),
        ...(Array.isArray(p.countries) ? p.countries : []),
      ].join(' ');
      const hay = `${p.title} ${p.description} ${p.category} ${p.departure} ${extra}`.toLowerCase();
      if (!hay.includes(dest)) return false;
    }

    const deps = Array.isArray(p.departures) ? p.departures : [];

    if (hasMonth) {
      const okMonth = deps.some((dep) => {
        const d = parseFlexibleDate(dep.departureDate);
        if (isNaN(d.getTime())) return false;
        return d.getMonth() + 1 === monthNum;
      });
      if (!okMonth) return false;
    }

    if (hasDuration) {
      const okDur = deps.some((dep) => {
        const days = tripDays(dep);
        if (days < 0) return false;
        return matchesDuration(days, filters.duration);
      });
      if (!okDur) return false;
    }

    return true;
  });
}
